import { useState, useRef, useCallback } from 'react';
import { Search, X, Loader2, Briefcase, Sparkles, Plus } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import type { ESCOOccupationMatch } from '@/types';

interface OccupationMatcherProps {
  /** The text to analyze (target_group or admission_requirements) */
  value: string;
  /** Called when the text changes */
  onChange: (value: string) => void;
  /** Existing matched occupations */
  matchedOccupations?: ESCOOccupationMatch[];
  /** Called when matched occupations change */
  onOccupationsChange?: (occupations: ESCOOccupationMatch[]) => void;
  /** Field label */
  label: string;
  /** Placeholder text */
  placeholder: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Number of textarea rows */
  rows?: number;
  /** Help text */
  helpText?: string;
}

export default function OccupationMatcher({
  value,
  onChange,
  matchedOccupations = [],
  onOccupationsChange,
  label,
  placeholder,
  disabled = false,
  rows = 3,
  helpText,
}: OccupationMatcherProps) {
  // Use parent state directly - this is a controlled component
  // No local state needed, which avoids sync issues and feedback loops

  // Search state
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ESCOOccupationMatch[]>([]);
  const [searching, setSearching] = useState(false);

  // Auto-suggest state
  const [suggesting, setSuggesting] = useState(false);
  const [suggestError, setSuggestError] = useState('');

  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Search ESCO occupations
  const searchOccupations = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearching(true);

    try {
      const response = await authenticatedFetch(
        `/api/esco/local/occupations/search?q=${encodeURIComponent(query)}&language=no&limit=10`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.occupations) {
          setSearchResults(data.occupations.map((o: any) => ({
            uri: o.uri,
            code: o.code,
            title: o.title,
            description: o.description,
            score: o.relevance,
            title_lang: o.title_lang,
            description_lang: o.description_lang,
          })));
        }
      }
    } catch (err) {
      console.error('Occupation search failed:', err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  // Debounced search
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      searchOccupations(query);
    }, 300);
  };

  // Add occupation
  const addOccupation = (occupation: ESCOOccupationMatch) => {
    if (matchedOccupations.some(o => o.uri === occupation.uri)) {
      return; // Already added
    }

    onOccupationsChange?.([...matchedOccupations, occupation]);
    setSearchQuery('');
    setSearchResults([]);
    setShowSearch(false);
  };

  // Remove occupation
  const removeOccupation = (uri: string) => {
    if (disabled) return;
    onOccupationsChange?.(matchedOccupations.filter(o => o.uri !== uri));
  };

  // Auto-suggest occupations from text
  const autoSuggest = async () => {
    if (!value.trim() || disabled) return;

    setSuggesting(true);
    setSuggestError('');

    try {
      const response = await authenticatedFetch('/api/esco/local/match-occupations', {
        method: 'POST',
        body: JSON.stringify({
          text: value,
          max_results: 5,
        }),
      });

      if (!response.ok) {
        throw new Error('Kunne ikke analysere teksten');
      }

      const data = await response.json();

      if (data.success && data.matches) {
        // Filter out already added occupations
        const newMatches = data.matches.filter(
          (m: any) => !matchedOccupations.some(o => o.uri === m.uri)
        );

        if (newMatches.length > 0) {
          onOccupationsChange?.([
            ...matchedOccupations,
            ...newMatches.map((m: any) => ({
              uri: m.uri,
              code: m.code,
              title: m.title,
              description: m.description,
              score: m.score,
              title_lang: m.title_lang,
              description_lang: m.description_lang,
            })),
          ]);
        } else if (data.matches.length > 0) {
          setSuggestError('Alle foreslåtte yrkesroller er allerede lagt til');
        } else if (data.reason === 'general_public') {
          // Backend detekterte at målgruppen er en generell publikums-
          // beskrivelse (ikke profesjonell). Ingen yrkesroller skal foreslås.
          setSuggestError(
            data.message ||
            'Beskrivelsen peker på privatpersoner/allmennheten, ikke en spesifikk yrkesgruppe. ESCO-yrkesroller er ikke relevante her.'
          );
        } else if (data.reason === 'no_matches_above_threshold') {
          setSuggestError(
            data.message ||
            'Ingen ESCO-yrkesroller hadde tilstrekkelig samsvar med beskrivelsen (≥65%). Beskrivelsen er trolig for generell eller peker ikke på en profesjonell rolle.'
          );
        } else {
          setSuggestError('Ingen yrkesroller funnet i teksten');
        }
      }
    } catch (err: any) {
      console.error('Auto-suggest failed:', err);
      setSuggestError(err.message || 'Kunne ikke analysere teksten');
    } finally {
      setSuggesting(false);
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="label">{label}</label>
        {!disabled && value.trim() && (
          <button
            type="button"
            onClick={autoSuggest}
            disabled={suggesting}
            className="text-xs text-kf-primary hover:text-kf-primary-dark flex items-center gap-1"
          >
            {suggesting ? (
              <>
                <Loader2 size={12} className="animate-spin" />
                Analyserer...
              </>
            ) : (
              <>
                <Sparkles size={12} />
                Finn yrkesroller
              </>
            )}
          </button>
        )}
      </div>

      <textarea
        className="input w-full"
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
      />

      {helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}

      {suggestError && (
        <p className="text-xs text-amber-600">{suggestError}</p>
      )}

      {/* Task #310 (2026-07-02): Split i to seksjoner — auto (grønn) og suggest (grå).
          Foreslåtte yrker er borderline-matcher som brukeren aktivt må bekrefte. */}
      {(() => {
        const autoOccupations = matchedOccupations.filter(o => !(o as any).is_suggestion);
        const suggestedOccupations = matchedOccupations.filter(o => (o as any).is_suggestion);
        return (
          <>
            {autoOccupations.length > 0 && (
              <div className="mt-2">
                <div className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Briefcase size={12} />
                  Tilknyttede yrkesroller ({autoOccupations.length})
                </div>
                <div className="flex flex-wrap gap-1">
                  {autoOccupations.map((occupation) => (
                    <span
                      key={occupation.uri}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 border border-indigo-200 rounded text-xs"
                      title={occupation.description ? `${occupation.title}\n\n${occupation.description}` : occupation.title}
                    >
                      <Briefcase size={10} />
                      <span className="max-w-48 truncate" title={occupation.title}>{occupation.title}</span>
                      {((occupation as any).title_lang === 'en' || (occupation as any).description_lang === 'en') && (
                        <span
                          className="inline-flex items-center px-1 py-0 rounded text-[8px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                          title={
                            (occupation as any).title_lang === 'en'
                              ? 'ESCO mangler norsk oversettelse. Viser engelsk.'
                              : 'Tittel er norsk, men ESCO mangler norsk beskrivelse.'
                          }
                        >
                          EN
                        </span>
                      )}
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => removeOccupation(occupation.uri)}
                          className="ml-0.5 hover:text-red-600"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {suggestedOccupations.length > 0 && (
              <div className="mt-2 p-2 bg-gray-50 border border-gray-300 rounded">
                <div className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Briefcase size={12} />
                  Foreslåtte yrkesroller ({suggestedOccupations.length}) — klikk for å bekrefte tilknytning
                </div>
                <div className="text-[10px] text-gray-500 mb-2">
                  Disse yrkene ligger tett opp mot studieplanen (grenseland i semantisk eller nivå-match). Bekreft de du mener passer.
                </div>
                <div className="flex flex-wrap gap-1">
                  {suggestedOccupations.map((occupation) => (
                    <span
                      key={occupation.uri}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-white text-gray-700 border border-dashed border-gray-400 rounded text-xs"
                      title={(occupation as any).tier_reason
                        ? `${occupation.title}\n\n${(occupation as any).tier_reason}${occupation.description ? '\n\n' + occupation.description : ''}`
                        : (occupation.description ? `${occupation.title}\n\n${occupation.description}` : occupation.title)}
                    >
                      <Briefcase size={10} />
                      <span className="max-w-48 truncate">{occupation.title}</span>
                      {!disabled && (
                        <>
                          <button
                            type="button"
                            onClick={() => {
                              // Godta forslag: fjern is_suggestion-flagg
                              const updated = matchedOccupations.map(o =>
                                o.uri === occupation.uri
                                  ? { ...o, is_suggestion: false } as typeof o
                                  : o
                              );
                              onOccupationsChange?.(updated);
                            }}
                            className="ml-0.5 text-green-600 hover:text-green-800"
                            title="Bekreft tilknytning"
                          >
                            ✓
                          </button>
                          <button
                            type="button"
                            onClick={() => removeOccupation(occupation.uri)}
                            className="ml-0.5 text-red-500 hover:text-red-700"
                            title="Avvis forslag"
                          >
                            <X size={10} />
                          </button>
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        );
      })()}

      {/* Add occupation manually */}
      {!disabled && (
        <div className="mt-2">
          {showSearch ? (
            <div className="p-2 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Search size={14} className="text-gray-400" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Søk etter ESCO-yrkesrolle..."
                  className="flex-1 text-sm border-0 bg-transparent focus:ring-0 p-0"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowSearch(false);
                      setSearchQuery('');
                      setSearchResults([]);
                    }
                  }}
                />
                {searching && <Loader2 size={14} className="animate-spin text-gray-400" />}
                <button
                  type="button"
                  onClick={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              </div>

              {searchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-y-auto">
                  {searchResults.map((occupation) => (
                    <button
                      key={occupation.uri}
                      type="button"
                      onClick={() => addOccupation(occupation)}
                      className="w-full text-left p-2 hover:bg-gray-100 rounded text-sm border-b border-gray-100 last:border-0"
                    >
                      <div className="flex items-center gap-2">
                        <Briefcase size={14} className="text-indigo-500 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 flex items-center gap-2" title={occupation.title}>
                            <span>{occupation.title}</span>
                            {((occupation as any).title_lang === 'en' || (occupation as any).description_lang === 'en') && (
                              <span
                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                                title={
                                  (occupation as any).title_lang === 'en'
                                    ? 'ESCO mangler norsk oversettelse. Viser engelsk.'
                                    : 'Tittel er norsk, men ESCO mangler norsk beskrivelse.'
                                }
                              >
                                EN
                              </span>
                            )}
                          </div>
                          {occupation.description && (
                            <div className="text-xs text-gray-500 line-clamp-2" title={occupation.description}>{occupation.description}</div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
                <div className="mt-2 text-sm text-gray-500 text-center py-2">
                  Ingen resultater for "{searchQuery}"
                </div>
              )}
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowSearch(true)}
              className="text-xs text-gray-500 hover:text-kf-primary flex items-center gap-1"
            >
              <Plus size={12} />
              Legg til yrkesrolle manuelt
            </button>
          )}
        </div>
      )}
    </div>
  );
}
